import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Fade, Stack } from '@mui/material';
import { useIntl } from 'react-intl';
import Icon from '@mui/material/Icon';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import {
  useGetJobExecutionWithContextQuery,
  useGetStepDetailsWithContextQuery
} from 'services/batchMonitoringApi';
import CustomProgressBar from 'components/CustomProgressBar';
import { BATCH_STATUS, BatchUtils } from 'pages/knowledgeBase/batch/batchUtils';
import { formatDuration, parseDate } from 'utils/formatTime';

/**
 * Component for monitoring batch execution status and progress
 *
 * @component
 * @param {Object} props - The properties object
 * @param {Object} props.batch - The batch being monitored
 * @param {number} props.jobExecutionId - The job execution ID to monitor
 * @returns {React.Component} The Batch Monitor component
 */
function BatchMonitor({ batch, jobExecutionId }) {
  const { formatMessage: msg } = useIntl();
  const [pollingInterval, setPollingInterval] = useState(2000);
  const [stats, setStats] = useState({
    readCount: 0,
    writeCount: 0,
    skipCount: 0,
    errorCount: 0,
    totalCount: 0,
    progress: 0
  });

  const { data: jobExecution } = useGetJobExecutionWithContextQuery(jobExecutionId, {
    pollingInterval: pollingInterval,
    skip: !jobExecutionId || Boolean(!pollingInterval)
  });

  const { data: stepExecutions } = useGetStepDetailsWithContextQuery(jobExecutionId, {
    pollingInterval: pollingInterval,
    skip: !jobExecutionId || Boolean(!pollingInterval)
  });

  useEffect(() => {
    const jobFinished = jobExecution && BatchUtils.isFinished(jobExecution.status);
    if (jobFinished) {
      const stepFinished =
        stepExecutions &&
        stepExecutions.length > 0 &&
        BatchUtils.isFinished(stepExecutions[stepExecutions.length - 1]?.status);
      if (stepFinished) {
        setPollingInterval(0);
      }
    }
  }, [jobExecution, stepExecutions]);

  useEffect(() => {
    if (stepExecutions && stepExecutions.length > 0) {
      const currentStep = stepExecutions[stepExecutions.length - 1];
      setStats({
        readCount: currentStep.readCount || 0,
        writeCount: currentStep.writeCount || 0,
        skipCount: currentStep.skipCount || 0,
        errorCount: currentStep.errorCount || 0,
        totalCount: currentStep.totalItemCount || 0,
        progress:
          currentStep.totalItemCount > 0
            ? Math.min((currentStep.writeCount / currentStep.totalItemCount) * 100, 100)
            : 0
      });
    }
  }, [stepExecutions]);

  if (!batch || !jobExecution) {
    return null;
  }

  // Parse job execution dates
  const jobStartTime = parseDate(jobExecution.startTime);
  const jobEndTime = parseDate(jobExecution.endTime);

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox
        sx={{
          p: 2,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {getStatusIcon(jobExecution.status)}
            <MDTypography variant="h6">
              {batch.batchType?.label || `Batch ${batch.id}`}
            </MDTypography>
            <Chip
              label={msg({
                id: 'batch.monitor.chip.' + jobExecution.status,
                defaultMessage: jobExecution.status
              })}
              color={BatchUtils.getStatusColor(jobExecution.status)}
              size="small"
              sx={{ color: 'white !important', fontWeight: 'bold' }}
            />
          </Stack>

          <Box>
            <Stack direction="row" justifyContent="space-between" mb={1}>
              <MDTypography variant="body2" color="text.secondary">
                {msg({ id: 'batch.monitor.progress' })}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {jobExecution?.status === BATCH_STATUS.COMPLETED
                  ? '100 %'
                  : stats.progress === null
                    ? 'En cours...'
                    : `${stats.progress.toFixed(1)} %`}
              </MDTypography>
            </Stack>
            <CustomProgressBar
              color={jobExecution?.status === BATCH_STATUS.COMPLETED ? 'success' : 'primary'}
              value={jobExecution?.status === BATCH_STATUS.COMPLETED ? 100 : stats.progress}
              height={8}
            />

            <Stack direction="row" justifyContent="space-between" mt={0.5}>
              <MDTypography variant="caption" color="text">
                {stats.readCount?.toLocaleString() || '0'} /{' '}
                {stats.totalCount?.toLocaleString() || '0'}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {msg({ id: 'batch.monitor.items' })}
              </MDTypography>
            </Stack>
          </Box>

          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <MDTypography variant="body2" color="text">
                {msg({ id: 'batch.monitor.read' })}
              </MDTypography>
              <MDTypography variant="body2">{(stats.readCount || 0).toLocaleString()}</MDTypography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <MDTypography variant="body2" color="text">
                {msg({ id: 'batch.monitor.written' })}
              </MDTypography>
              <MDTypography variant="body2">
                {(stats.writeCount || 0).toLocaleString()}
              </MDTypography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <MDTypography variant="body2" color="text">
                {msg({ id: 'batch.monitor.skipped' })}
              </MDTypography>
              <MDTypography variant="body2">
                {(stats.skipCount - stats.errorCount).toLocaleString()}
              </MDTypography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <MDTypography variant="body2" color="error">
                {msg({ id: 'batch.monitor.errors' })}
              </MDTypography>
              <MDTypography variant="body2" color="error">
                {(stats.errorCount || 0).toLocaleString()}
              </MDTypography>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
            >
              <MDTypography variant="body2" fontWeight="bold">
                {msg({ id: 'batch.monitor.total' })}
              </MDTypography>
              <MDTypography variant="body2" fontWeight="bold">
                {(stats.totalCount || 0).toLocaleString()}
              </MDTypography>
            </Stack>
          </Stack>

          {jobStartTime && (
            <Stack spacing={0.5}>
              <Stack direction="row" justifyContent="space-between">
                <MDTypography variant="body2" color="text">
                  {msg({ id: 'batch.monitor.start.time' })}
                </MDTypography>
                <MDTypography variant="body2">{jobStartTime.toLocaleString()}</MDTypography>
              </Stack>

              {jobEndTime && (
                <Stack direction="row" justifyContent="space-between">
                  <MDTypography variant="body2" color="text">
                    {msg({ id: 'batch.monitor.end.time' })}
                  </MDTypography>
                  <MDTypography variant="body2">{jobEndTime.toLocaleString()}</MDTypography>
                </Stack>
              )}

              {jobExecution.status === BATCH_STATUS.STARTED && (
                <Stack direction="row" justifyContent="space-between">
                  <MDTypography variant="body2" color="text">
                    {msg({ id: 'batch.monitor.estimated.remaining' })}
                  </MDTypography>
                  <MDTypography variant="body2">
                    {formatDuration(
                      computeRemainingTime(jobStartTime, stats.readCount, stats.totalCount)
                    )}
                  </MDTypography>
                </Stack>
              )}

              {jobStartTime && (
                <Stack direction="row" justifyContent="space-between">
                  <MDTypography variant="body2" color="text">
                    {msg({ id: 'batch.monitor.duration' })}
                  </MDTypography>
                  <MDTypography variant="body2">
                    {formatDuration((jobEndTime || new Date()).getTime() - jobStartTime.getTime())}
                  </MDTypography>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </MDBox>
    </Fade>
  );

  function getStatusIcon(status) {
    return (
      <Icon fontSize="large" color={BatchUtils.getStatusColor(status)}>
        {BatchUtils.getStatusIcon(status)}
      </Icon>
    );
  }

  function computeRemainingTime(time, readCount, totalItemCount) {
    const startTime = parseDate(time);
    if (startTime) {
      const elapsedMs = Date.now() - startTime.getTime();
      const itemsPerMs = readCount / elapsedMs;
      const remainingItems = Math.max(0, (totalItemCount || 0) - readCount);
      if (itemsPerMs > 0) {
        return remainingItems / itemsPerMs;
      }
    }
    return null;
  }
}

BatchMonitor.propTypes = {
  batch: PropTypes.shape({
    id: PropTypes.number,
    batchType: PropTypes.object
  }),
  jobExecutionId: PropTypes.number
};

export default BatchMonitor;
